/**
 * Dashboard (F3c) — porte de src/pages/Dashboard do app atual: os MESMOS
 * indicadores (packages/core/services/dashboardService). Gráficos feitos à mão
 * (como no Pedidos), sem biblioteca. "Vendas por linha" passa de pizza para
 * barras horizontais com % (mesmos dados, leitura mais fácil).
 */
import { createResource, For, type JSX, Show } from 'solid-js'
import { api, versaoDados } from '../nucleo/cliente'
import { brl, num, Vazio } from '../ui/kit'
import { BarrasHorizontais, Colunas } from './pedidos/Graficos'
import { moedaCurta } from './pedidos/formato'

function Cartao(props: { rotulo: string; valor: string | number; dica?: string; aviso?: boolean }) {
  return (
    <div class="rounded-xl border border-edge bg-panel p-4">
      <div class="text-[11px] uppercase tracking-wide text-ink-subtle">{props.rotulo}</div>
      <div class="mt-1 text-xl font-semibold tabular-nums" classList={{ 'text-warning': props.aviso, 'text-ink': !props.aviso }}>{props.valor}</div>
      <Show when={props.dica}>
        <div class="mt-0.5 text-xs text-ink-subtle">{props.dica}</div>
      </Show>
    </div>
  )
}

function Quadro(props: { children: JSX.Element }) {
  return <div class="rounded-xl border border-edge bg-panel p-4">{props.children}</div>
}

export function Dashboard() {
  const [d] = createResource(() => versaoDados(), () => api.dashboard.data())
  return (
    <div class="flex flex-col gap-5 p-4 sm:p-6">
      <div>
        <h1 class="text-xl font-semibold text-ink">Dashboard</h1>
        <p class="text-xs text-ink-subtle">Indicadores gerenciais.</p>
      </div>
      <Show when={d.latest} fallback={<Vazio titulo={d.loading ? 'Carregando…' : 'Sem dados.'} />}>
        {(x) => (
          <>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Cartao rotulo="Estoque a preço de venda" valor={brl(x().estoque.valorVendaBRL)} />
              <Cartao rotulo="Estoque a custo (landed)" valor={brl(x().estoque.valorCustoBRL)} />
              <Cartao rotulo="Abaixo do mínimo" valor={x().estoque.abaixoMinimo} aviso />
              <Cartao rotulo="Margem média" valor={x().margemMediaPercent != null ? `${num(x().margemMediaPercent)}%` : '—'} dica="preço × custo landed" />
              <Cartao rotulo="Vendas efetivadas" valor={x().vendas.confirmadas} dica={brl(x().vendas.valorTotal)} />
              <Cartao rotulo="Ticket médio" valor={brl(x().vendas.ticketMedio)} />
              <Cartao rotulo="Importações recebidas" valor={x().importacoes.recebidas} dica={`${x().importacoes.emTransito} em trânsito · ${x().importacoes.pedido} em pedido`} />
              <Cartao rotulo="Lead time médio" valor={x().importacoes.leadTimeMedioDias != null ? `${num(x().importacoes.leadTimeMedioDias)} dias` : '—'} />
            </div>
            <Show
              when={x().vendas.confirmadas > 0}
              fallback={
                <div class="rounded-xl border border-edge bg-panel p-6 text-center text-sm text-ink-subtle">
                  Ainda não há vendas efetivadas. Os gráficos de vendas por mês, por linha e top clientes aparecem aqui assim que houver vendas confirmadas.
                </div>
              }
            >
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Quadro>
                  <Colunas
                    titulo="Vendas por mês"
                    barras={x().vendas.porMes.map((m) => ({ rotulo: m.mes, valor: m.valor, texto: moedaCurta(m.valor), dica: `${m.mes}: ${brl(m.valor)}` }))}
                  />
                </Quadro>
                <Quadro>
                  <BarrasHorizontais
                    titulo="Vendas por linha de produto"
                    barras={(() => {
                      const total = x().porLinha.reduce((s, l) => s + l.valor, 0) || 1
                      return x().porLinha.map((l) => ({
                        rotulo: l.linha,
                        valor: l.valor,
                        texto: `${((l.valor / total) * 100).toFixed(0)}%`,
                        dica: `${l.linha}: ${brl(l.valor)} (${((l.valor / total) * 100).toFixed(1)}%)`,
                      }))
                    })()}
                  />
                </Quadro>
                <Quadro>
                  <div class="text-xs font-medium uppercase tracking-wide text-ink-subtle mb-2">Top 10 clientes por volume de compra</div>
                  <table class="w-full text-sm">
                    <tbody>
                      <For each={x().topClientes}>
                        {(c, i) => (
                          <tr class="border-b border-edge-muted last:border-0">
                            <td class="py-1.5 w-6 text-ink-subtle tabular-nums">{i() + 1}</td>
                            <td class="py-1.5 text-ink">{c.nome}</td>
                            <td class="py-1.5 text-right font-medium tabular-nums text-ink">{brl(c.total)}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Quadro>
              </div>
            </Show>
          </>
        )}
      </Show>
    </div>
  )
}
